import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, Upload } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { config } from "../../../config";
import SearchAndFilters from "../../../components/SearchAndFilters";
import SearchContentContainer from "../../../components/SearchContentContainer";
import { filterConfigs } from "../../../components/SearchAndFilters/config";
import LoadingSkeleton from "../../../components/LoadingSkeleton";
import { InitialSearchStates } from "../../../components/SearchAndFilters/searchStates";
import {
  getPlatformColorClass,
  getPlatformDisplayName,
} from "../../../utils/Convert";
import { Alert, Box, Button, IconButton, Typography } from "@mui/material";
import useWebSocket from "../../../utils/Sockets";
import AdInsightsService from "../../../services/AdInsights";
import GenerationProgress from "../../../components/GenerationProgress";
import queryString from "query-string";
import { homeRoutePath, settingsPath } from "../../Router";
import SessionService from "../../../services/Session";
import { setUser } from "../../../redux/actions/user";
import { logIssueNetworkRequestError } from "../../../utils/Loggers";
import { useDispatch } from "react-redux";
import SubscriptionLimitDialog from "../../../components/SubscriptionLimitDialog";
import { useSubscriptionLimit } from "../../../hooks/useSubscriptionLimit";
import { displayToastMessage } from "../../../utils/Toasts";

const AdInsightsUploadDialog = ({
  projectId,
  open,
  onClose,
  onUpload,
  isUploading,
}: any) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [modelAPIKey, setModelAPIKey] = useState("");
  const [strategyId, setStrategyId] = useState("");
  const [error, setError] = useState<string>("");

  const handleFileChange = (e: Event | any) => {
    const file = e.target.files[0];
    setError("");
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !strategyId) {
      setError("Please select a file and provide the Strategy ID");
      return;
    }

    try {
      await onUpload(selectedFile, strategyId, modelAPIKey);
      setSelectedFile(null);
      setModelAPIKey("");
      setStrategyId("");
      setError("");
      onClose();
    } catch (error: any) {
      setError(error.message || "Failed to upload file");
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 ${
        open ? "flex" : "hidden"
      } items-center justify-center bg-black/70 backdrop-blur-sm`}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md m-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 flex justify-between items-center border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Upload className="h-5 w-5 text-red-600" />
            Upload Campaign Data
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Strategy ID Input Section */}
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              Strategy ID
            </label>
            <div className="space-y-3">
              <input
                type="text"
                value={modelAPIKey}
                onChange={(e) => setModelAPIKey(e.target.value)}
                placeholder="Enter Model API Key"
                className="w-full rounded-lg border border-gray-300 py-3 px-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
              />
              <input
                type="text"
                value={strategyId}
                onChange={(e) => setStrategyId(e.target.value)}
                placeholder="Enter Strategy ID"
                className="w-full rounded-lg border border-gray-300 py-3 px-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
              />
              <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-red-500 text-sm">
                <p className="text-gray-700">
                  First, please enter the Ad Strategy ID to analyze. You can
                  find this ID by visiting the{" "}
                  <a
                    href={`/project/${projectId}`}
                    className="text-red-600 hover:underline font-medium"
                  >
                    Ad Strategy Agent section
                  </a>{" "}
                  and selecting a strategy.
                </p>
                <p className="mt-3 text-gray-700">
                  Next, upload your campaign data (for multiple campaigns, merge
                  all campaign data into a single CSV file).
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-800 mb-2">
              Upload File
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-200 border-dashed rounded-lg hover:border-red-400 transition-colors bg-gray-50">
              <div className="space-y-2 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label className="relative cursor-pointer rounded-md font-medium text-red-600 hover:text-red-500">
                    <span>Select a file</span>
                    <input
                      type="file"
                      className="sr-only"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  CSV or Excel files up to 10MB
                </p>
                {selectedFile && (
                  <div className="flex items-center justify-center gap-2 text-sm mt-2 bg-red-50 text-red-600 py-1 px-3 rounded-full">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{selectedFile.name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="px-6 mb-4">
            <Alert
              severity="error"
              className="rounded-lg font-medium"
              sx={{
                border: "1px solid rgba(211, 47, 47, 0.2)",
                borderRadius: "8px",
              }}
            >
              {error}
            </Alert>
          </div>
        )}

        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-2xl border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || !strategyId || isUploading}
            className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 rounded-lg hover:from-red-700 hover:to-red-800 shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm"
          >
            {isUploading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Uploading...
              </span>
            ) : (
              "Upload"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const InsightCard = ({ insight, onClick, index }: any) => {
  const patterns = insight.insights.patterns?.platformSpecific || [];
  const recommendations = insight.insights.recommendations || {};

  // Get an array of platforms
  const platforms = patterns.map((p: any) => p.platform);

  // Calculate total pattern count
  const totalPatterns = patterns.reduce(
    (sum: number, platform: any) => sum + platform.patterns.length,
    0
  );

  return (
    <motion.div
      className="h-full rounded-xl bg-white border border-gray-200 hover:border-red-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      onClick={() => onClick(insight)}
    >
      <div className="p-6">
        <div className="flex justify-between items-start mb-5">
          <div>
            <h3 className="text-lg font-semibold mb-1 text-gray-900">
              {insight.name}
            </h3>
            <div className="flex items-center gap-2">
              <span className="bg-red-50 text-red-600 text-xs font-medium px-2.5 py-1 rounded-full">
                {totalPatterns} Pattern{totalPatterns !== 1 ? "s" : ""}
              </span>
              <span className="text-gray-500 text-sm">
                {new Date(insight.createdAt || Date.now()).toLocaleDateString(
                  "en-US",
                  {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  }
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 mb-5">
          <div>
            <p className="text-gray-500 text-xs uppercase font-medium mb-2">
              Platforms Analyzed
            </p>
            <div className="flex flex-wrap gap-2">
              {platforms.map((platform: any) => (
                <span
                  key={platform}
                  className={`px-3 py-1 rounded-full text-xs font-medium 
                    ${getPlatformColorClass(platform)}`}
                >
                  {getPlatformDisplayName(platform)}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-gray-500 text-xs uppercase font-medium mb-2">
                Budget Adjustments
              </p>
              <p className="font-medium">
                {recommendations.budgetAdjustments?.length || 0}
                {recommendations.budgetAdjustments?.length !== 1
                  ? " Adjustments"
                  : " Adjustment"}
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase font-medium mb-2">
                Top Opportunity
              </p>
              <p className="font-medium line-clamp-2 text-sm">
                {patterns[0]?.patterns[0]?.improvement ||
                  "No opportunities found"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
          <span className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium flex items-center hover:bg-red-100 transition-colors">
            View Details
            <svg
              className="w-4 h-4 ml-1"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const InsightsList = ({ data, onInsightSelect }: any) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <AnimatePresence>
        {data.map((insight: any, index: number) => (
          <InsightCard
            key={index}
            insight={insight}
            index={index}
            onClick={onInsightSelect}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

const AdInsightsView = ({ onBack }: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [userId, setUserId] = useState<any>("");
  const [insights, setInsights] = useState<Array<any>>([]);
  const [searchState, setSearchState] = useState(InitialSearchStates.insights);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Progress tracking state
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [generationStage, setGenerationStage] = useState("");
  const [generationError, setGenerationError] = useState("");
  const [jobState, setJobState] = useState<any>(null);
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState("");

  const { dialogProps, makeRequest: makeSubscriptionGatedRequest } =
    useSubscriptionLimit(() => navigate(settingsPath, { replace: true }));

  const onProgress = useCallback((data: any) => {
    if (data.jobType === "adInsights") {
      setProgress(data.progress);
      setGenerationStage(data.stage);
      setLastUpdateTimestamp(data.timestamp);
      setJobState("in-progress");
      if (data.progress === 100) {
        setJobState("completed");
      }
    }
  }, []);

  const onJobCompleted = useCallback(
    (data: any) => {
      if (data.type === "adInsights") {
        setProgress(100);
        setJobState("completed");
        setGenerationStage("Analysis Complete");
        setLastUpdateTimestamp(new Date().toISOString());
        window.location.reload();
      }
    },
    [searchState]
  );

  const { socket, connected } = useWebSocket({
    onProgress,
    onJobCompleted,
    onJobFailed: (error) => {
      setProgress(-1);
      setJobState("error");
      setGenerationError(error.message);
      setLastUpdateTimestamp(new Date().toISOString());
    },
  });

  const fetchInsights = useCallback(
    async (userId: string | any, searchParams?: any) => {
      setLoading(true);
      setError("");

      try {
        if (!config.inProd) {
          setInsights(config.mocks.insights);
          return;
        }

        const adInsightsService = new AdInsightsService();
        const response = await adInsightsService.listInsights(projectId!, {
          userId,

          // Main query parameters
          name: searchParams.query,

          // Pagination
          page: searchParams?.pagination?.currentPage,
          limit: searchParams?.pagination?.limit,

          // Sorting
          sortBy: searchParams?.sorting?.sortBy || "createdAt",
          sortOrder: searchParams?.sorting?.sortOrder === -1 ? "desc" : "asc",

          // Date filters
          dateFrom: searchParams?.filters?.dateFrom,
          dateTo: searchParams?.filters?.dateTo,
        });

        setInsights(response.insights);
        setSearchState((prevState) => ({
          ...prevState,
          pagination: response.pagination,
        }));
      } catch (error: any) {
        console.error("Error fetching insights:", error);
        setError(error.message || "Failed to load insights");
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  const handleInitialLoad = useCallback(async () => {
    try {
      const parsedQuery = queryString.parse(location.search);
      if (parsedQuery.email && parsedQuery.view === "activate") {
        navigate(homeRoutePath, { replace: true });
        return;
      }

      const sessionService = new SessionService();
      const sessionData = await sessionService.reviewSession();
      if (!sessionData.success) {
        navigate(homeRoutePath, { replace: true });
        return;
      }

      dispatch(setUser(sessionData.userSessionData));
      setUserId(sessionData.userSessionData.id);

      await fetchInsights(sessionData.userSessionData.id, searchState);
    } catch (error: any) {
      logIssueNetworkRequestError("AdStrategyView.handleInitialLoad", error);
      navigate(homeRoutePath, { replace: true });
    }
  }, [dispatch, location, navigate, fetchInsights]);

  useEffect(() => {
    handleInitialLoad();
  }, [handleInitialLoad]);

  const handleSearch = async (searchParams: any) => {
    setSearchState(searchParams);
    await fetchInsights(userId, searchParams);
  };

  const handleUpload = async (
    file: any,
    strategyId: string,
    modelAPIKey: string
  ) => {
    setIsUploading(true);
    setGenerating(true);
    setJobState("initializing");
    setProgress(0);
    setGenerationStage("Initializing Analysis");
    setLastUpdateTimestamp(new Date().toISOString());
    setGenerationError("");

    try {
      const adInsightsService = new AdInsightsService();
      await makeSubscriptionGatedRequest({
        requestFn: () =>
          adInsightsService.startInsightsGeneration(projectId!, {
            userId,
            strategyId,
            modelAPIKey,
            campaignData: file,
            socketId: socket?.id,
          }),
        onSuccess: (_) => {
          displayToastMessage(
            "success",
            "Insights generation successfully started"
          );
        },
        onError: (error: any) => {
          console.error("Failed to start insights generation", error);
          let errorMsg = error.error ? error.error : "";
          setJobState("error");
          setGenerationError(
            `Failed to start insights generation. Please try again. Error: ${errorMsg}`
          );
          setProgress(-1);
          setLastUpdateTimestamp(new Date().toISOString());
        },
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="p-6 pb-10 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <Box
          sx={{
            mb: { xs: 3, sm: 4, md: 5 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 2, sm: 2 },
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: { xs: 1, sm: 0 },
            }}
          >
            <IconButton
              onClick={() => {
                if (onBack) onBack();
                navigate(`/project/${projectId}`);
              }}
              sx={{
                padding: { xs: 0.5, sm: 1 },
                bgcolor: "white",
                boxShadow: 1,
                "&:hover": {
                  bgcolor: "white",
                  boxShadow: 2,
                },
              }}
            >
              <ArrowLeft className="text-gray-800" />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: "1.5rem", sm: "1.75rem" },
                fontWeight: "600",
                color: "#111827",
              }}
            >
              Ad Insights
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<RefreshCw className="h-4 w-4" />}
              onClick={() => fetchInsights(userId, searchState)}
              className="text-gray-800 border-gray-300 hover:bg-gray-50"
              sx={{
                flex: { xs: 1, sm: "initial" },
                padding: { xs: "8px 16px", sm: "10px 20px" },
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: "500",
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Upload className="h-4 w-4" />}
              onClick={() => setShowUploadDialog(true)}
              disabled={generating}
              className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
              sx={{
                flex: { xs: 1, sm: "initial" },
                padding: { xs: "8px 16px", sm: "10px 20px" },
                fontSize: { xs: "0.875rem", sm: "0.875rem" },
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: "500",
              }}
            >
              Upload Campaign Data
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert
            severity="error"
            className="mb-6 rounded-lg"
            sx={{
              border: "1px solid rgba(211, 47, 47, 0.2)",
              borderRadius: "10px",
            }}
          >
            {error}
          </Alert>
        )}

        {jobState && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 overflow-hidden">
            <GenerationProgress
              title="Analyzing Campaign Data"
              description="Please wait while we analyze your campaign data and generate insights..."
              progress={progress}
              stage={generationStage}
              timestamp={lastUpdateTimestamp}
              jobType="adInsights"
              jobState={jobState}
              error={generationError}
            />
          </div>
        )}

        <div className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <SearchAndFilters
              totalShown={insights.length}
              onSearch={handleSearch}
              pagination={searchState.pagination}
              sorting={searchState.sorting}
              filters={searchState.filters}
              filterConfig={filterConfigs.insights}
              isLoading={loading}
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <SearchContentContainer
            isLoading={loading}
            data={insights}
            LoadingComponent={LoadingSkeleton}
            ContentComponent={InsightsList}
            componentProps={{
              onInsightSelect: (insight: any) =>
                navigate(`/project/${projectId}/adinsights/${insight._id}`),
            }}
            loadingText="Loading insights..."
            emptyTitle="No Insights Found"
            emptyMessage="Try adjusting your search or filter criteria"
          />
        </div>

        <AdInsightsUploadDialog
          projectId={projectId}
          open={showUploadDialog}
          onClose={() => setShowUploadDialog(false)}
          onUpload={handleUpload}
          isUploading={isUploading}
        />

        <SubscriptionLimitDialog {...dialogProps} />
      </div>
    </div>
  );
};

export default AdInsightsView;
